import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { QueryKeys } from 'src/hooks/QueryKeys';
import { useNavigate } from 'react-router-dom';
import {
  Bin,
  DuplicateIcon,
  EditIcon,
  IOption,
  Export,
  Card as ItemCard,
  MenuIcon,
  ViewIcon,
  SnackbarStatus,
  useSnackbar,
  Prompt,
  Tooltip,
  Typography,
} from '@weareopenr/component-library';
import { boldMenuName } from 'src/helpers/truncateMenuName/boldMenuName';
import { MenuInUsePrompt } from 'src/components/MenuInUsePrompt';
import { SpinnerIcon } from 'src/components/SpinnerIcon';
import { useDeleteMenuPrompt } from 'src/hooks/useDeleteMenuPrompt/useDeleteMenuPrompt';
import { IMenuLabel } from 'src/api/getMenuLabels';
import { menuLabelChips } from 'src/components/MenuLabelsField';
import PublishModal from '../PublishModal';
import MenuModal from '../MenuModal';
import styles from './MenuCard.module.scss';

export interface MenuCardProps {
  menuId: string;
  name: string;
  image?: string;
  consumerName?: string | null;
  menuLabels?: IMenuLabel[];
  sectionCount?: number;
}

/** The stacked Card's width, for the grid the menu cards sit in. */
export const MENU_CARD_WIDTH = 258;

const MenuCard = ({ menuId, name, image, consumerName, menuLabels = [], sectionCount }: MenuCardProps) => {
  const [variant, setVariant] = useState<'update' | 'duplicate'>('update');
  const [isMenuModalVisible, setIsMenuModalVisible] = useState<boolean>(false);
  const [isPublishModalVisible, setIsPublishModalVisible] = useState<boolean>(false);
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();
  const removePromptTitle = `Are you sure you want to delete "${name}"?`;
  const navigate = useNavigate();
  const handleMenuModalToggle = (value: 'duplicate' | 'update'): void => {
    setVariant(value);
    setIsMenuModalVisible((prev) => !prev);
  };

  const {
    isRemovePromptVisible,
    isMenuInUsePromptVisible,
    menuInUseErrorMessage,
    isSubmitting,
    isCheckingCanDelete,
    openDeletePrompt,
    closeDeletePrompt,
    closeMenuInUsePrompt,
    confirmDelete,
  } = useDeleteMenuPrompt({
    menuId,
    genericErrorMessage: 'We have been unable to complete your request. Please try again',
    onDeleted: async () => {
      showSnackbar({
        status: SnackbarStatus.SUCCESS,
        message: <>You have successfully deleted &quot;{boldMenuName(name)}&quot; menu.</>,
      });
      await queryClient.invalidateQueries({ queryKey: [QueryKeys.menus] });
    },
  });

  const itemEllipsisMenu: IOption[] = [
    {
      id: 0,
      name: 'View',
      icon: ViewIcon,
      onOptionClick: () => {
        navigate(`/menus/${menuId}`);
      },
    },
    {
      id: 1,
      name: 'Edit',
      icon: EditIcon,
      onOptionClick: () => {
        handleMenuModalToggle('update');
      },
    },
    {
      id: 2,
      name: 'Publish',
      icon: Export,
      onOptionClick: () => {
        setIsPublishModalVisible(true);
      },
    },
    {
      id: 3,
      name: 'Duplicate',
      icon: DuplicateIcon,
      onOptionClick: () => {
        handleMenuModalToggle('duplicate');
      },
    },
    { id: 'd-1', isDivider: true },
    {
      id: 4,
      name: 'Delete',
      icon: isCheckingCanDelete ? SpinnerIcon : Bin,
      hoverColour: 'tomato',
      onOptionClick: openDeletePrompt,
      isDisabled: isCheckingCanDelete,
      persistMenuOnClick: true,
      dataTestId: 'open-remove-prompt',
    },
  ];

  return (
    <div data-testid="card">
      <MenuModal
        title={`${variant === 'update' ? 'Edit' : 'Duplicate'} menu`}
        menuName={name}
        closeModal={() => setIsMenuModalVisible(false)}
        open={isMenuModalVisible}
        variant={variant}
        id={menuId}
        consumerName={consumerName}
        image={image}
        menuLabels={menuLabels}
      />
      <PublishModal
        isOpen={isPublishModalVisible}
        closeModal={() => setIsPublishModalVisible(false)}
        menuId={menuId}
        menuName={name}
      />
      <Prompt
        promptHeading={removePromptTitle}
        closePrompt={closeDeletePrompt}
        open={isRemovePromptVisible}
        buttonHandler={confirmDelete}
        buttonText="Delete"
        buttonColour="tomato"
        backButton={true}
        backButtonText="Cancel"
        size="small"
        isSubmitting={isSubmitting}
      >
        <Typography size="medium" weight="bold" className={styles['spacing-paragraphs']}>
          Deleting this menu will <u>not</u> delete any product.
        </Typography>
        <Typography size="small">This action cannot be undone.</Typography>
      </Prompt>
      <MenuInUsePrompt
        open={isMenuInUsePromptVisible}
        closePrompt={closeMenuInUsePrompt}
        message={menuInUseErrorMessage}
        menuName={name}
      />
      <ItemCard
        layout="stacked"
        label="Menu"
        text={name}
        subtext={consumerName}
        key={menuId}
        imageSrc={image}
        to={`/menus/${menuId}`}
        ellipsisOptions={itemEllipsisMenu}
        icon={MenuIcon}
        variant="tertiary"
        details={sectionCount === undefined ? [] : [{ text: 'Menu sections', detail: String(sectionCount) }]}
        withDivider
        reserveFootnote
        footnote={
          menuLabels.length > 0 && (
            // The row only fits a few chips, so hovering lists every label.
            <Tooltip text={menuLabels.map(({ name }) => name).join('\n')} asChild>
              <div className={styles['menu-labels']} data-testid="menu-labels">
                {menuLabelChips(menuLabels)}
              </div>
            </Tooltip>
          )
        }
      />
    </div>
  );
};

export default MenuCard;
